// Client-side URL classification for KaziPeople Web. Mirrors the server
// (auth-service BrowserService.classifyUrl) for instant UX, but the SERVER is
// authoritative — anything mutating or trust-sensitive re-checks server-side.
// Generalized from src/lib/providerLinks.ts (HTTPS-first, no embedded creds).
import type { BrowserPolicy, Capability, UrlClassification } from './types';
import { providerEmbedFor } from './providerEmbeds';

const UNSAFE_SCHEMES = /^(javascript|data|file|blob|vbscript):/i;

// host matches an entry when equal or a subdomain of it (case-insensitive).
function matchesDomain(host: string, entries: string[]): boolean {
  const h = host.toLowerCase();
  return entries.some((e) => {
    const d = String(e).trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^\./, '').replace(/\/.*$/, '');
    return !!d && (h === d || h.endsWith('.' + d));
  });
}

export function classifyUrl(raw: string, policy?: BrowserPolicy | null): UrlClassification {
  const trimmed = (raw ?? '').trim();
  if (UNSAFE_SCHEMES.test(trimmed)) {
    return { capability: 'UNSAFE_URL', url: trimmed, domain: null, reason: 'This link uses an unsafe scheme.' };
  }
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return { capability: 'UNSAFE_URL', url: trimmed, domain: null, reason: 'That is not a valid web address.' };
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') {
    return { capability: 'UNSAFE_URL', url: trimmed, domain: null, reason: `Unsupported scheme "${u.protocol}".` };
  }
  if (u.username || u.password) {
    return { capability: 'UNSAFE_URL', url: trimmed, domain: u.hostname || null, reason: 'This link hides credentials in the address.' };
  }
  if (!u.hostname) {
    return { capability: 'UNSAFE_URL', url: trimmed, domain: null, reason: 'This address has no site.' };
  }
  const domain = u.hostname.toLowerCase();
  if (policy) {
    if (policy.blocklist?.length && matchesDomain(domain, policy.blocklist)) {
      return { capability: 'BLOCKED_BY_POLICY', url: u.toString(), domain, reason: 'Your organization has blocked this site.' };
    }
    if (policy.allowlist?.length && !matchesDomain(domain, policy.allowlist)) {
      return { capability: 'BLOCKED_BY_POLICY', url: u.toString(), domain, reason: 'This site is not on your organization allowlist.' };
    }
    if (policy.allowExternal === false) {
      return { capability: 'BLOCKED_BY_POLICY', url: u.toString(), domain, reason: 'External browsing is turned off for your organization.' };
    }
  }
  // Phase 2 (first slice): a provider that PUBLISHES an embeddable form gets
  // embedded; everything else keeps the honest external card. Policy is
  // checked above, so a blocked domain never reaches this — an embeddable
  // provider cannot smuggle a site past the allowlist.
  const embed = providerEmbedFor(u.toString());
  if (embed) {
    return { capability: 'EMBED_SUPPORTED', url: u.toString(), domain, embed };
  }
  // No published embed form → external. Not a failure: see providerEmbeds.ts.
  return { capability: 'EXTERNAL_ONLY', url: u.toString(), domain };
}

// Normalize free-form address-bar input: bare "nmbbank.co.tz" → "https://nmbbank.co.tz".
// Returns null when it clearly isn't a URL (caller can treat it as a search term).
export function coerceUrl(input: string): string | null {
  const s = (input ?? '').trim();
  if (!s) return null;
  if (UNSAFE_SCHEMES.test(s)) return null;
  if (/^https?:\/\//i.test(s)) return s;
  // looks like a domain (has a dot, no spaces) → assume https
  if (/^[^\s]+\.[^\s]{2,}(\/|$)/.test(s)) return `https://${s}`;
  return null;
}

// Open an external URL safely in a new tab (noopener,noreferrer).
export function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

// The favicon for a domain, via Google's public S2 service (no server call from us).
export function faviconFor(domain: string | null): string | null {
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
}

export const CAPABILITY_IS_OPENABLE = (c: Capability): boolean =>
  c === 'EXTERNAL_ONLY' || c === 'EMBED_SUPPORTED' || c === 'PREVIEW_SUPPORTED';
